







































































































































import { mapGetters } from 'vuex';
import {
  TIMEZONES,
  aiStreamerJobStatusMap,
  TAiStreamer
} from '@/views/CopyStake/CopyStakeAiStreamer/types';
import { formatTimeDuration } from '@/helpers/timeDurationHelper';
import formatDate from '@/helpers/formatDate';
import {
  getAiStreamerGames,
  getAiStreamerJobDetails,
  getAiStreamerTemplates,
  getAiStreamerVoices,
  getAiStreamerAvatarsList // check
} from '@/api/CopyStake/AiStreamer';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import {
  EAiStreamerAvatarService,
  EAiStreamerJobStatus,
  IAiStreamerJobDetailsRecord,
  TAiStreamerAvatarRecord,
  TAiStreamerGameData,
  TAiStreamerTemplate,
  TAiStreamerVoiceRecord
} from '@/api/schema';
import { NO_AVATAR_ID } from '@/views/CopyStake/CopyStakeAiStreamer/AiStreamers/CopyStakeAiStreamerEditModal.vue';

type TJobDetailsState = {
  statusMap: Record<EAiStreamerJobStatus, { label: string; type: string }>;
  jobDetails: IAiStreamerJobDetailsRecord | null;
  initialAiStreamerDetails: TAiStreamer | null;
  supportedGames: TAiStreamerGameData[];
  aiStreamerTemplates: TAiStreamerTemplate[];
  avatarsList: TAiStreamerAvatarRecord[];
  voicesList: TAiStreamerVoiceRecord[];
  loading: Record<string, boolean>;
};

const VStatus = (): Promise<any> => import('@/components/VStatus.vue');

export default {
  name: 'CopyStakeAiStreamerJobDetails',
  components: {
    VStatus
  },

  data(): TJobDetailsState {
    return {
      statusMap: aiStreamerJobStatusMap,
      jobDetails: null,
      initialAiStreamerDetails: null,
      supportedGames: [],
      aiStreamerTemplates: [],
      avatarsList: [],
      voicesList: [],
      loading: {
        jobDetails: false,
        initialAiStreamerDetails: false,
        supportedGames: false,
        aiStreamerTemplates: false,
        avatars: false,
        voices: false
      }
    };
  },
  watch: {
    requestParams: {
      async handler(): Promise<void> {
        await this.fetchJobDetailsData();
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapGetters('Onboarding', [
      'operatorId',
      'isUndergroundCasinoGamesIntegrationType'
    ]),
    requestParams(): any {
      return {
        jobId: this.jobId,
        operatorId: this.operatorId
      };
    },
    jobId(): number | null {
      return +this.$route.params?.jobId || null;
    },
    timezonesMap(): Record<string, string> {
      return TIMEZONES.reduce((all, curr) => {
        all[curr.value] = curr.text.split(' ')[0];

        return all;
      }, {});
    },
    isLoading(): boolean {
      return Object.values(this.loading).some(Boolean);
    }
  },
  methods: {
    formatTimeDuration,
    convertDate(date: string, zone: string): string {
      return `${formatDate(date, 'yyyy-MM-dd HH:mm', false, false)} ${
        this.timezonesMap[zone]
      }`;
    },
    formatAvatar(avatarId: string): string {
      const found = this.avatarsList?.find(
        (avatar: TAiStreamerAvatarRecord) => avatar.avatarId === avatarId
      );
      return found?.avatarName || '-';
    },
    formatVoice(data: TAiStreamer): string {
      const found = this.voicesList?.find(
        (voice: TAiStreamerVoiceRecord) => voice.voiceId === data.voiceId
      );

      return found
        ? `${found.name} (${found.gender}) (${found.voiceId})`
        : '-';
    },
    formatSupportedGames(games: { id: number; name: string }[]): string {
      const names = (games || [])
        .map((game) => game.name)
        .filter(Boolean);

      if (names.length === 0) return '-';

      return names.length === 1 ? names[0] : names.join(', ');
    },
    formatSupportedTemplate(templateId: number): string {
      const found = this.aiStreamerTemplates?.find(
        (t) => t.id === templateId
      );
      return found?.name || '-';
    },
    async fetchJobDetailsData(): Promise<void> {
      if (!this.operatorId || !this.jobId) return;

      try {
        this.loading.jobDetails = true;
        this.loading.initialAiStreamerDetails = true;

        const jobData = await getAiStreamerJobDetails(
          this.requestParams.jobId
        );

        this.jobDetails = jobData || null;

        this.parseAiStreamerConfigDetails(jobData);
      } catch (error) {
        errorToastMessage(error);

        this.jobDetails = null;
      } finally {
        this.loading.jobDetails = false;
      }
    },
    async parseAiStreamerConfigDetails(
      jobData: IAiStreamerJobDetailsRecord
    ): Promise<void> {
      const initialConfigJson = jobData?.initialConfig || null;
      if (!initialConfigJson) return;

      try {
        this.loading.initialAiStreamerDetails = true;

        const parsedAiStreamerDetails = initialConfigJson
          ? JSON.parse(initialConfigJson)
          : null;

        this.initialAiStreamerDetails = parsedAiStreamerDetails || null;

        this.fetchAiStreamerSupportedGames();
        this.fetchAiStreamerTemplates();
        this.fetchAiStreamerAvatars();
        this.fetchAiStreamerVoices();
      } catch (error) {
        errorToastMessage(error);

        this.initialAiStreamerDetails = null;
      } finally {
        this.loading.initialAiStreamerDetails = false;
      }
    },
    async fetchAiStreamerSupportedGames(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.supportedGames = true;

        const gamesData = await getAiStreamerGames();

        this.supportedGames = gamesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.supportedGames = [];
      } finally {
        this.loading.supportedGames = false;
      }
    },
    async fetchAiStreamerTemplates(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.aiStreamerTemplates = true;

        const templatesData = await getAiStreamerTemplates();

        this.aiStreamerTemplates = templatesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.aiStreamerTemplates = [];
      } finally {
        this.loading.aiStreamerTemplates = false;
      }
    },
    async fetchAiStreamerAvatars(): Promise<void> {
      if (!this.jobDetails.autoclickerConfigId) return;

      try {
        this.loading.avatars = true;

        const avatarsData = await getAiStreamerAvatarsList();

        this.avatarsList = avatarsData || [];
      } catch (error) {
        errorToastMessage(error);

        this.avatarsList = [];
      } finally {
        this.loading.avatars = false;
      }
    },
    async fetchAiStreamerVoices(): Promise<void> {
      if (
        !this.jobDetails.autoclickerConfigId ||
        !this.initialAiStreamerDetails.voiceLanguage
      )
        return;

      try {
        this.loading.voices = true;

        const voicesData = await getAiStreamerVoices(
          this.initialAiStreamerDetails?.avatarService ||
            EAiStreamerAvatarService.ELEVEN_LABS,
          this.initialAiStreamerDetails.voiceLanguage
        );

        this.voicesList = voicesData || [];
      } catch (error) {
        errorToastMessage(error);

        this.voicesList = [];
      } finally {
        this.loading.voices = false;
      }
    }
  }
};
